import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Building2, ExternalLink, Copy } from "lucide-react";

const emptyForm = { nama: "", website: "", api_key: "" };

export default function Cabang() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cabang");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(emptyForm);
    setAddOpen(true);
  };

  const openEdit = (c) => {
    setForm({ nama: c.nama, website: c.website, api_key: c.api_key });
    setEditItem(c);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/cabang", form);
      toast.success("Cabang berhasil ditambahkan");
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/cabang/${editItem.id}`, form);
      toast.success("Cabang berhasil diperbarui");
      setEditItem(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/cabang/${deleteItem.id}`);
      toast.success("Cabang berhasil dihapus");
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = (key) => {
    navigator.clipboard?.writeText(key);
    toast.success("API Key disalin");
  };

  const maskKey = (k) => (k.length > 10 ? `${k.slice(0, 4)}••••${k.slice(-4)}` : k);

  return (
    <div className="p-8" data-testid="cabang-page">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Pengaturan</span>
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight">Cabang</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Kelola daftar cabang beserta website dan API key-nya.
          </p>
        </div>
        <Button onClick={openAdd} data-testid="add-cabang-button">
          <Plus className="mr-2 h-4 w-4" />
          Tambah Cabang
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-surface/40 fade-up" data-testid="cabang-table-container">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Building2 className="h-6 w-6 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">Belum ada cabang.</p>
          </div>
        ) : (
          <Table data-testid="cabang-table">
            <TableHeader>
              <TableRow>
                <TableHead className="uppercase tracking-[0.1em] text-xs">Nama Cabang</TableHead>
                <TableHead className="uppercase tracking-[0.1em] text-xs">Link Website</TableHead>
                <TableHead className="uppercase tracking-[0.1em] text-xs">API Key</TableHead>
                <TableHead className="text-right uppercase tracking-[0.1em] text-xs">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`cabang-row-${c.id}`}>
                  <TableCell className="font-medium">{c.nama}</TableCell>
                  <TableCell>
                    <a
                      href={c.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {c.website}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => copyKey(c.api_key)}
                      className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
                      data-testid={`copy-cabang-key-${c.id}`}
                    >
                      {maskKey(c.api_key)}
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-cabang-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteItem(c)} data-testid={`delete-cabang-${c.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-cabang-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tambah Cabang Baru</DialogTitle>
            <DialogDescription>Isi data cabang di bawah ini.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="add-nama">Nama Cabang</Label>
              <Input id="add-nama" data-testid="add-cabang-nama-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-website">Link Website</Label>
              <Input id="add-website" data-testid="add-cabang-website-input" type="url" placeholder="https://" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-apikey">API Key</Label>
              <Input id="add-apikey" data-testid="add-cabang-apikey-input" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} required />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="add-cabang-submit-button">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent data-testid="edit-cabang-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Cabang</DialogTitle>
            <DialogDescription>Perbarui data cabang.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nama">Nama Cabang</Label>
              <Input id="edit-nama" data-testid="edit-cabang-nama-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-website">Link Website</Label>
              <Input id="edit-website" data-testid="edit-cabang-website-input" type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-apikey">API Key</Label>
              <Input id="edit-apikey" data-testid="edit-cabang-apikey-input" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} required />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="edit-cabang-submit-button">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Perbarui
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent data-testid="delete-cabang-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Hapus cabang?</AlertDialogTitle>
            <AlertDialogDescription>
              Cabang <strong>{deleteItem?.nama}</strong> akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cabang-cancel-button">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-cabang-confirm-button"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
